import React from 'react';

import {LinkifyFormatter} from '../../../../../../../../src/components/common/formatters/markdown/linkifyFormatter';
import {LinkTrap} from '../../../../../../../../src/components/common/linkTrap/linkTrap';
import {DateCell} from '../../../../../../../../src/components/common/table/cells/dateCell';
import {InboxCellContainer} from '../../../../../../../../src/components/common/table/cells/inboxCellContainer';
import {TeammateCellContainer} from '../../../../../../../../src/components/common/table/cells/teammateCellContainer';
import {TextCell} from '../../../../../../../../src/components/common/table/cells/textCell';
import {TableColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {CustomFieldsEnum} from '../../../../../../../../src/models/api/customFieldModel';
import {buildCustomFieldLabel} from '../../../../../../../../srcCommon/helpers/models/customFieldHelpers';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {ContactsManagerCustomFieldColumnMetadata} from './contactsManagerCustomFieldTypes';

/*
 * Interfaces.
 */

interface RenderCustomFieldCellOptions<T> {
  customFieldValue: string | undefined;
  index: number;
  column: TableColumn<T, ContactsManagerCustomFieldColumnMetadata>;
  isHovering: boolean;
  isSelected: boolean;
  shouldShowStickyGradient: boolean;
  options: TableRowRenderOptions;
  isPreviousSiblingSelected?: boolean;
  isNextSiblingSelected?: boolean;
  onTeammateSelect?: HandlerOf<string>;
}

/*
 * Helpers.
 */

export function renderCustomFieldCell<T>({
  customFieldValue,
  index,
  column,
  isHovering,
  isSelected,
  shouldShowStickyGradient,
  options,
  isPreviousSiblingSelected,
  isNextSiblingSelected,
  onTeammateSelect,
}: RenderCustomFieldCellOptions<T>) {
  switch (column.metadata?.customField.type) {
    case CustomFieldsEnum.INBOX:
      return (
        <InboxCellContainer
          key={`custom-field-${column.id}`}
          inboxId={customFieldValue}
          settings={column.settings}
          isHovering={isHovering}
          isSelected={isSelected}
          size={options.size}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
        />
      );
    case CustomFieldsEnum.TEAMMATE:
      return (
        <TeammateCellContainer
          key={`custom-field-${column.id}`}
          teammateEmail={customFieldValue}
          settings={column.settings}
          isHovering={isHovering}
          isSelected={isSelected}
          avatarSize={VisualSizesEnum.SMALL}
          size={options.size}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          onSelect={(event, contactUid) => {
            if (event.defaultPrevented || !onTeammateSelect) {
              return;
            }
            event.preventDefault();
            onTeammateSelect(contactUid);
          }}
        />
      );
    case CustomFieldsEnum.DATETIME:
      return (
        <DateCell
          key={`custom-field-${column.id}`}
          value={customFieldValue}
          settings={column.settings}
          isHovering={isHovering}
          isSelected={isSelected}
          size={options.size}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
        />
      );
    case CustomFieldsEnum.BOOLEAN:
      return (
        <TextCell
          key={`custom-field-${column.id}`}
          value={customFieldValue}
          settings={column.settings}
          isHovering={isHovering}
          isSelected={isSelected}
          size={options.size}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          renderValue={convertBooleanToDisplayText}
        />
      );
    case CustomFieldsEnum.ENUM: {
      return (
        <TextCell
          key={`custom-field-${column.id}`}
          value={customFieldValue}
          settings={column.settings}
          isHovering={isHovering}
          isSelected={isSelected}
          size={options.size}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          renderValue={(value) =>
            column.metadata ? buildCustomFieldLabel(value, column.metadata.customField) : value
          }
        />
      );
    }

    case CustomFieldsEnum.STRING:
    default: {
      return (
        <TextCell
          key={`custom-field-${column.id}`}
          value={customFieldValue}
          settings={column.settings}
          isHovering={isHovering}
          isSelected={isSelected}
          size={options.size}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
          shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
          shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          renderValue={(value) => (
            <LinkTrap>
              <LinkifyFormatter text={value} />
            </LinkTrap>
          )}
        />
      );
    }
  }
}

const convertBooleanToDisplayText = (value: string): any =>
  value === 'true' ? (
    <Trans id="contacts-manager-table-custom-field-value-yes">Yes</Trans>
  ) : (
    <Trans id="contacts-manager-table-custom-field-value-no">No</Trans>
  );
